import { useCallback, useEffect, useMemo } from "react";
import {
  noBarData,
  noData,
  validLineData,
  horizontalBarChartMargins,
  chartColorScale,
  createDateRange,
  isUserDeleted,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { useQuery } from "@tanstack/react-query";
import { fetchMessagePeople, getEngagementQuery, getLineChartQuery } from "./Queries/queries";

import styles from "./messagePerformanceBody.module.scss";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { bindAction } from "interfaces";
import { setHasDataToExport, setExportAction } from "features/Reporting/state/export/exportSlice";
import { bindActionCreators } from "@reduxjs/toolkit";
import { type ColumnOption } from "interfaces/columnOptions";
import { type MessagePerson } from "../types/models";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { doMessageDrillDownExport } from "../state/thunks/messagePerformanceThunk";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { type PaginationParams } from "features/Library/Common/models";
import dateTimeUtils from "utils/dateTimeUtils";
import moment from "moment";

export type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  messageId: number;
  messageName: string;
  selectedSendSessionId: number;
  selectedSendSessionDateTime: string;
  includeAccounts: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

type EngagementQueryResult = {
  cards: CardReportingItem[];
  usageBarData: DataPoint[];
};

type PeopleColumnOption = ColumnOption<MessagePerson> & { sortKey?: string };

const getColumnOptions = (
  accountId: number,
  includeAccounts: boolean,
  childAccountId: number | undefined,
  hasCreateAccountsAddOn: boolean | undefined,
): PeopleColumnOption[] => {
  const columns: PeopleColumnOption[] = [
    {
      name: "Name",
      width: 5,
      isSortable: false,
      render(entity) {
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink =
          !isUserDeleted(entity.UserName) && (!includeAccounts || childAccountEnabled || !childAccountId);
        const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
          entity.AccountId,
          accountId,
          childAccountId,
          hasCreateAccountsAddOn,
        );
        const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
          childAccountEnabled,
          isUserFromCurrentAccount,
          childAccountId,
          fallbackAccountId: entity.AccountId,
        });
        const userName = <Tooltip target={<TextTruncate>{entity.UserName}</TextTruncate>} content={entity.UserName} />;
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                accountId={linkAccountId}
                to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
                target={linkTarget}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
          </div>
        );
      },
    },
    {
      isSortable: false,
      name: "Email",
      render(entity) {
        return (
          <div>
            <Tooltip target={<TextTruncate>{entity.Email}</TextTruncate>} content={entity.Email} />
          </div>
        );
      },
      width: 4,
    },
    {
      isSortable: true,
      name: "Send Date",
      sortKey: "SendDate",
      render(entity) {
        const dateTime = entity.SendDate ? dateTimeUtils.formatDate(entity.SendDate) : "–";
        return (
          <div>
            <Tooltip target={<TextTruncate>{dateTime}</TextTruncate>} content={dateTime} />
          </div>
        );
      },
      width: 3,
    },
    {
      isSortable: true,
      name: "Click Date",
      sortKey: "ClickDate",
      render(entity) {
        const dateTime = entity.ClickDate ? dateTimeUtils.formatDate(entity.ClickDate) : "–";
        return (
          <div>
            <Tooltip target={<TextTruncate>{dateTime}</TextTruncate>} content={dateTime} />
          </div>
        );
      },
      width: 3,
    },
  ];

  return columns;
};

export const MessagePerformanceDrillDownBody: React.FC<Props> = ({
  filter,
  messageId,
  messageName,
  selectedSendSessionId,
  selectedSendSessionDateTime,
  setHasExportData,
  setExportAction,
  messageExport,
  accountId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["messageDrillDownLineChart", filter, messageId, chartPeriod, selectedSendSessionId],
    queryFn: getLineChartQuery,
    enabled: chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["messageDrillDownEngagement", filter, messageId, selectedSendSessionId],
    queryFn: getEngagementQuery,
    enabled: filter.type !== undefined,
  });

  const columnOptions = useMemo(
    () => getColumnOptions(accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn),
    [accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn],
  );

  const peopleQueryKey = useMemo(
    () => ["messageDrillDownPeople", filter, messageId, selectedSendSessionId] as const,
    [filter, messageId, selectedSendSessionId],
  );

  const fetchPeopleData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) =>
      fetchMessagePeople(filter, messageId, params, selectedSendSessionId, signal),
    [filter, messageId, selectedSendSessionId],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This send session contains no data.",
      }),
    [setHasExportData],
  );

  const barDomain = useMemo(() => {
    if (engagementQuery.isSuccess && engagementQuery.data) {
      const data = engagementQuery.data as EngagementQueryResult;
      let maxValue = Math.max(...data.usageBarData.map((d) => d.value));
      return [0, Math.max(maxValue, 1)];
    }
  }, [engagementQuery.isSuccess, engagementQuery.data]);

  // Setting up correct export method
  useEffect(() => {
    setExportAction({
      method: messageExport,
      args: [
        { ...filter, messageId },
        selectedSendSessionId,
        `${messageName} ${moment(selectedSendSessionDateTime).format("MM-DD-YYYY h-mm A")}`,
      ],
      isExportEnabled: true,
    });
  }, [
    filter,
    messageExport,
    setExportAction,
    messageId,
    messageName,
    selectedSendSessionId,
    selectedSendSessionDateTime,
  ]);

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper titles={["Activity"]} legendLabels={["Sends", "Clicks"]} showLegend colorRange={chartColorScale}>
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess && lineChartQuery.data
              ? (() => {
                  const data = lineChartQuery.data;
                  return validLineData([data.CumulativeSends, data.CumulativeClicks]) ? (
                    <LineChart
                      {...sharedAccountReportingLineProps}
                      xFormatterFunc={getDateFormatByCount(data.Date.length)}
                      colorRange={chartColorScale}
                      xData={[data.Date, data.Date]}
                      yData={[data.CumulativeSends, data.CumulativeClicks]}
                    />
                  ) : (
                    noData(filter)
                  );
                })()
              : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.engagementQuery}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && engagementQuery.data
              ? (() => {
                  const data = engagementQuery.data as EngagementQueryResult;
                  return !noBarData(...data.usageBarData.map((d) => d.value)) ? (
                    <HorizontalBarChart
                      {...sharedAccountReportingHorizontalBarProps}
                      margins={{ ...horizontalBarChartMargins }}
                      domain={barDomain}
                      data={data.usageBarData}
                    />
                  ) : (
                    noData(filter)
                  );
                })()
              : noData(filter)}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && engagementQuery.data && (
            <CardReporting items={(engagementQuery.data as EngagementQueryResult).cards} />
          )}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <GenericPerformanceListWithServerPagination
          title="People"
          columns={columnOptions}
          filter={filter}
          fetchPage={fetchPeopleData}
          queryKeyBase={peopleQueryKey}
          hasDataForExport={updateExportDataState}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  messageExport: bindActionCreators(doMessageDrillDownExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(MessagePerformanceDrillDownBody);
export default ConnectedComponent;
